/* EqDialog class
 * Helper class to provide Fancybox create_dialogality using the native HTML dialog element
 *
 * See MDN documentation for details on how the native element works
 * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/dialog
 */

const eq_dialog_defaults = {
    selector: '.eq-dialog',
    show_close_button: true,
    close_button_text: 'Close dialog',
    aria_label: 'Dialog',
    // remove_inline_content: takes inline divs used for content and removes duplicates
    remove_inline_content: false,
    style: {
        width: '800px',
    },
    onOpen: null,
    description: false
}
// Default HTML template for dialog content
eq_dialog_defaults.template_inner = `<div class="eq-dialog-inner"></div>`;
eq_dialog_defaults.template_close_button = `<form class="eq-close-btn" method="dialog"><button autofocus aria-label="{{close_btn_txt}}"></button></form>`;
eq_dialog_defaults.template_video = `<video class="eq-dialog-video" controls autoplay>` +
    `<source src="{{src}}" type="{{format}}" />` +
    'Sorry, your browser doesn\'t support embedded videos, <a href="{{src}}">download</a> and watch with your favorite video player!' +
    `</video>`;
eq_dialog_defaults.template_video_a11y = (videoData) => {
        return `
    <div class="eq-video-wrapper">
    <video id="row-video-${videoData.unique_id || 'default'}" 
            class="video-js vjs-fluid ${!videoData.video_has_audio ? 'vjs-video-no-audio' : ''}"
            ${!videoData.decorative_video ? 'controls' : ''}
            preload="auto"
            crossorigin="anonymous"
            ${videoData.title ? `data-video-title="${videoData.title}"` : ''}
            data-standard-source="${videoData.video_file || ''}"
            ${videoData.video_with_audio_description ? `data-audio-description-source="${videoData.video_with_audio_description}"` : ''}
            ${videoData.image ? `poster="${videoData.image}"` : ''}
            ${videoData.video_description ? `aria-label="${videoData.video_description}"` : ''}
            ${!videoData.video_has_audio ? 'muted' : ''}
            ${videoData.decorative_video ? 'playsinline autoplay muted loop' : ''}
            src="${videoData.video_file || ''}"
            >
        <source src="${videoData.video_file || ''}" type="video/mp4">
        Sorry, your browser doesn't support embedded videos, <a href="${videoData.video_file || ''}">download</a> and watch with your favorite video player!
    </video>
    ${videoData.video_file && videoData.decorative_video && !videoData.page_banner_video ? 
        `<button class="video-control-btn" data-target="row-video-${videoData.unique_id || 'default'}" aria-label="Pause Video"></button>` : ''}
    </div>
    ${videoData.transcript || videoData.disclosure_statement || videoData.manual_transcript ? 
    `<div class="inline-btn media-documents">
        ${videoData.transcript ? 
            `<a class="result-link link media-document-btn" href="${videoData.transcript}" target="_blank" aria-label="${videoData.title || 'Video'} Video Transcript PDF" title="Opens in a new window"><span class="eqicon-PDF" aria-hidden="true"></span>Video Transcript</a>` : ''}
        ${videoData.disclosure_statement ? 
            `<a class="result-link link media-document-btn" href="${videoData.disclosure_statement}" target="_blank" aria-label="${videoData.title || 'Video'} Disclosure Statement PDF" title="Opens in a new window"><span class="eqicon-PDF" aria-hidden="true"></span>Disclosure Statement</a>` : ''}
        ${videoData.manual_transcript ? 
            `<button class="result-link link media-document-btn transcript-trigger" id="transcript-trigger-${videoData.unique_id || 'default'}" data-video-title="${videoData.title || ''}" aria-label="View Text Transcript for ${videoData.title || 'Video'}"><span class="eqicon-DOC" aria-hidden="true"></span>View Text Transcript</button>` : ''}
    </div>` : ''}
    ${videoData.manual_transcript ? 
    `<div class="eq-transcript-container" id="transcript-container-${videoData.unique_id || 'default'}">
        <div class="eq-transcript-wrapper" id="transcript-wrapper-${videoData.unique_id || 'default'}" tabindex="0" role="region" aria-label="Text Transcript for ${videoData.title || 'Video'}">
            <div class="transcript-content" id="transcript-content-${videoData.unique_id || 'default'}">
                ${videoData.manual_transcript}
            </div>
        </div>
    </div>` : ''}
    `;
    };
eq_dialog_defaults.template_vimeo_embedded_video = `<div class="eq-dialog-inner eq-video-wrapper">
        <img src="{{image}}" alt="{{alt_text}}">
        <a href="https://vimeo.com/{{src}}" target="_blank" rel="noopener" class="video-btn paused" aria-label="Watch video"></a>
    </div>`;
eq_dialog_defaults.template_youtube_embedded_video = `<div class="eq-dialog-inner eq-video-wrapper">
    <img src="{{image}}" alt="{{alt_text}}">
    <a href="https://youtube.com/watch?v={{src}}" target="_blank" rel="noopener" class="video-btn paused" aria-label="Watch video"></a>
</div>`;


class EqDialog {
    constructor(options = {}) {
        this.settings = JSON.parse(JSON.stringify(eq_dialog_defaults));
        // override defaults if provided
        for (const prop in options) {
            this.settings[prop] = options[prop];
            if (prop === 'style') {
                for (const style_name in options.style) {
                    this.settings.style[style_name] = options.style[style_name];
                }
            }
        }
        
        // Listen for tab press, if modal is open keep focus locked
        document.addEventListener('keydown', (e) => {
            let dialog = document.querySelector('dialog[open]');
            if (!dialog) {
                return;
            }
            // Dynamically find all focusable elements and determine the first and last one
            let dialog_focusables = dialog.querySelectorAll('button,input,select,textarea,iframe,area,a,*[tabindex="0"]');
            let first_focus = dialog_focusables.item(0);
            let last_focus = dialog_focusables.item(dialog_focusables.length - 1);
            if (!e.shiftKey && e.key === 'Tab') {
                if (document.activeElement == last_focus) {
                    e.preventDefault();
                    first_focus.focus();
                }
            }
            if (e.shiftKey && e.key === 'Tab') {
                if (document.activeElement == first_focus) {
                    e.preventDefault();
                    last_focus.focus();
                }
            }
            if (first_focus == last_focus) {
                let close_button = dialog.querySelector("button");
                close_button.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        });
    }

    // Find all dialog buttons/links and generate HTML
    init() {
        let dialog_btns = document.querySelectorAll(this.settings.selector);
        dialog_btns.forEach((el) => {
            let dialog_type, dialog_id, href, src, textDescription;
            dialog_type = el.dataset.type ? el.dataset.type : false;
            // Determine source of dialog content from href attribute or data-target attribute
            href = el.getAttribute('href');
            src = href ? href : el.dataset.target ? el.dataset.target : false;
            textDescription = el.dataset.description ? el.dataset.description : false;
            
            if (!src) {
                console.error(`Could not determine the dialog source. Please set the attribute 'href' or 'data-target'`);
                return;
            }
            // Determine what kind of dialog is being created
            if (!dialog_type) {
                if (src.match(/(^data:image\/[a-z0-9+\/=]*,)|(\.(jp(e|g|eg)|gif|png|bmp|webp|svg|ico)((\?|#).*)?$)/i)) {
                    dialog_type = 'image';
                } else if (src.match(/\.(mp4|mov|ogg|webm)((\?|#).*)?$/i)) {
                    dialog_type = 'video';
                } else if (src.match(/\.(pdf)((\?|#).*)?$/i)) {
                    dialog_type = 'iframe';
                } else if (src[0] === '#') {
                    dialog_type = 'inline';
                } else {
                    console.error(`Could not determine the dialog type for ${src}. Please specify the attribute 'data-type' or 'href'.`);
                    return;
                }
            }
            // Create an ID to assign to the dialog, take the existing ID attribute if there is one
            dialog_id = dialog_type === 'inline' && src ? src.substring(1) : this.generate_dialog_id(dialog_type);
            
            // Converts URL links to anchor link to prevent navigating away from page
            if (href) {
                el.setAttribute('href', `#${dialog_id}`);
                el.setAttribute('role', 'button');
            }
            // Assign event listeners the link/button to open the modal
            el.addEventListener('click', (e) => {
                e.preventDefault();
                let dlg = this.create_dialog({
                    dialog_type: dialog_type,
                    dialog_id: dialog_id,
                    image: el.dataset.image ? el.dataset.image : '',
                    src: src,
                    img_alt: el.dataset.imgAlt ? el.dataset.imgAlt : '',
                    close_button_text: el.dataset.closebtntxt ? el.dataset.closebtntxt : this.settings.close_button_text,
                    aria_label: el.dataset.arialabel ? el.dataset.arialabel : this.settings.aria_label,
                    description: textDescription
                });
                if (!dlg) {
                    return;
                }
                dlg.showModal();
                if (typeof this.settings.onOpen === 'function') {
                    this.settings.onOpen(dlg);
                }
            });
        });
    }

    // Create and open a dialog immediately
    // The source is either the ID of a target element (without the #) or URL for the media file
    open(dialog_type, source, textDescription, img_alt = '') {
        if (dialog_type === 'inline' && !source) {
            console.error(`Inline dialogs must have a target.`);
            return;
        }
        if (dialog_type === 'image' || dialog_type === 'iframe' || dialog_type === 'video' && !source) {
            console.error(`Media dialogs must have a source.`);
            return;
        }
        let dlg = this.create_dialog({
            dialog_type: dialog_type,
            dialog_id: dialog_type === 'inline' ? source : this.generate_dialog_id(dialog_type),
            src: dialog_type === 'inline' ? '' : source,
            img_alt: img_alt,
            close_button_text: this.settings.close_button_text,
            description: textDescription
        });
        if (!dlg) {
            return;
        }
        dlg.showModal();
    }

    getVideoData(src) {
        // Find the element that triggered this dialog
        let triggerElement = document.querySelector(`[href="#${src}"]`);
        if (!triggerElement) return null;
        
        return {
            unique_id: triggerElement.dataset.uniqueId || 'video-' + Date.now(),
            title: triggerElement.dataset.videoTitle || '',
            video_file: triggerElement.dataset.videoFile || '',
            video_has_audio: triggerElement.dataset.videoHasAudio !== 'false',
            decorative_video: triggerElement.dataset.decorativeVideo === 'true',
            video_description: triggerElement.dataset.description || '',
            video_with_audio_description: triggerElement.dataset.audioDescription || '',
            image: triggerElement.dataset.image || '',
            transcript: triggerElement.dataset.transcript || '',
            disclosure_statement: triggerElement.dataset.disclosureStatement || '',
            manual_transcript: triggerElement.dataset.manualTranscript || '',
            page_banner_video: triggerElement.dataset.pageBannerVideo === 'true'
        };
    }

    /* Creates HTML dialog element with given parameters
     * Parameters:
     * dialog_id - ID of the target element the inline dialog will pull content from
     * src - URL of the image or iframe to be displayed
     * img_alt - Alt text to add to the image dialog
     */
    create_dialog(params = {}) {
        let settings = this.settings;
        let has_form = false;
        // Create element for dialog
        let dialog = document.createElement('dialog');
        jq(dialog).attr('aria-modal', 'true');
        jq(dialog).attr('aria-label', params.aria_label);
        dialog.classList.add('eq-dialog-wrapper');
        dialog.innerHTML = settings.template_inner;

        let dialog_content = '';
        if (settings.show_close_button) {
            let close_btn_html = settings.template_close_button;
            close_btn_html = close_btn_html.replace('{{close_btn_txt}}', params.close_button_text);
            dialog_content += close_btn_html;
        }
        if (params.dialog_type === 'inline') {
            let inline_div = document.getElementById(params.dialog_id);
            if (!inline_div) {
                console.error('Unable to find element with ID of \'#' + params.dialog_id + '\'');
                return false;
            }
            has_form = inline_div.querySelector('form');
            dialog_content += inline_div.innerHTML;
            // Hide target inline div to prevent duplicate ID error
            if (this.settings.remove_inline_content) {
                inline_div.remove();
            } else {
                inline_div.style.display = 'none';
            }
        }
        if (params.dialog_type === 'image') {
            dialog_content += `<img src="${params.src}" alt="${params.img_alt}">`;
        }

        if (params.dialog_type === 'video') {
            let matcher = /(?:\.([^.]+))?$/;
            let format = 'video/' + matcher.exec(params.src)[1];
            let vid_tpl = settings.template_video;
            vid_tpl = vid_tpl.replace('{{src}}', params.src);
            vid_tpl = vid_tpl.replace('{{format}}', format);
            dialog_content += vid_tpl;
        }

        if (params.dialog_type === 'video_a11y') {
            let video_data = this.getVideoData(params.dialog_id);
            let vid_tpl = eq_dialog_defaults.template_video_a11y(video_data);
            dialog_content += vid_tpl;
        }

        if (params.dialog_type === 'youtube') {
            let vid_tpl = settings.template_youtube_embedded_video;
            vid_tpl = vid_tpl.replace('{{image}}', params.image);
            vid_tpl = vid_tpl.replace('{{alt_text}}', params.alt_text);
            vid_tpl = vid_tpl.replace('{{src}}', params.src);
            dialog_content += vid_tpl;
        }

        if (params.dialog_type === 'vimeo') {
            let vid_tpl = settings.template_vimeo_embedded_video;
            vid_tpl = vid_tpl.replace('{{image}}', params.image);
            vid_tpl = vid_tpl.replace('{{alt_text}}', params.alt_text);
            vid_tpl = vid_tpl.replace('{{src}}', params.src);
            dialog_content += vid_tpl;
        }

        if (params.dialog_type === 'iframe') {
            dialog_content += `<iframe ${params.description ? 'aria-label="' + params.description + '"' : ''} src="${params.src}"></iframe>`;
        }

        // Apply styling
        let inner_el = dialog.querySelector('.eq-dialog-inner');
        for (const style_name in settings.style) {
            inner_el.style[style_name] = settings.style[style_name];
        }
        // Add content to dialog and add it to the DOM
        inner_el.innerHTML = dialog_content;
        document.body.appendChild(dialog);
        
        // Popup specific functionality
        if (has_form) {
            hCaptchaOnLoad();
            validate_forms();
        }

        // Add event listener to hide the dialog when it is closed
        dialog.addEventListener('close', () => {
            dialog.remove();
        });
        
        //Close dialog when link inside dialog is clicked
        jq(dialog)
            .find("a")
            .click(function () {
                   dialog.remove();
            });

        if (params.dialog_type === 'video_a11y') {
            this.initializeVideoJS(dialog);
        }

        return dialog;
    }

    generate_dialog_id(dialog_type) {
        let index = 0;
        let id = dialog_type + '-dialog-' + index;
        while (document.getElementById(id)) {
            index++;
            id = dialog_type + '-dialog-' + index;
        }
        return id;
    }

    initializeVideoJS(dialog) {
        // Check if Video.js is available
        if (typeof videojs !== 'undefined') {
            // Find all video elements in the dialog
            const videos = dialog.querySelectorAll('video.video-js');
            videos.forEach(video => {
                try {
                    // Initialize Video.js on each video
                    videojs(video);
                } catch (error) {
                    console.warn('Failed to initialize Video.js on video:', video, error);
                }
            });
        } else {
            console.warn('Video.js library not loaded. Videos may not display properly.');
        }
    }
}
